import debounce from './debounce';

export function setupDirectoryModule( container ) {
	const fetchUrl = container.dataset.fetchUrl;

	if ( ! fetchUrl ) {
		return;
	}

	const baseUrl = container.dataset.baseUrl;

	const forms = container.querySelectorAll( 'form' );
	const presets = container.querySelectorAll( '.js-preset-value' );

	const heading = container.querySelector( '.js-directory-heading' );
	const filterWrappers = container.querySelectorAll( '.js-directory-filters' );
	const statsWrapper = container.querySelector( '.js-directory-stats' );
	const navWrapper = container.querySelector( '.js-directory-nav' );
	const tagsWrapper = container.querySelector( '.js-directory-tags' );
	const resultsWrapper = container.querySelector( '.js-directory-results' );
	const paginationWrappers = container.querySelectorAll( '.js-directory-pagination' );
	const sortControl = container.querySelector( '.js-directory-sort' );
	const applyButton = container.querySelector( '.js-filter-apply' );
	const clearButton = container.querySelector( '.js-filter-clear' );
	const filterCount = container.querySelector( '.js-filter-count' );
	const filterSelection = container.querySelector( '.js-filter-selection' );
	const resetButton = container.querySelector( '.js-filter-reset' );
	const searchInput = container.querySelector( `[name="search"]` );

	const format = resultsWrapper.dataset.format;
	const columns = resultsWrapper.dataset.columns;
	const perPage = paginationWrappers[ 0 ]?.dataset.perpage;
	let curPage = paginationWrappers[ 0 ]?.dataset.page;

	const initParams = getQueryParams();
	const initParamKeys = [ ...initParams.keys() ];
	for ( const key of initParamKeys ) {
		const name = key.replace( /\[\d*\]/g, '' );
		if ( name !== key ) {
			for ( const value of initParams.getAll( key ) ) {
				initParams.append( name, value );
			}
			initParams.delete( key );
		}
	}

	presets.forEach( preset => {
		if ( ! initParams.has( preset.name ) ) {
			initParams.append( preset.name, preset.value );
		}
	} );

	if ( paginationWrappers[ 0 ] ) {
		initParams.append( 'paged', paginationWrappers[ 0 ].dataset.page );
	}

	history.replaceState( { query: initParams.toString() }, '' );

	function getQueryParams() {
		const params = new URLSearchParams();
		forms.forEach( form => {
			const data = new FormData( form );
			for ( const [ key, value ] of data.entries() ) {
				if ( value ) {
					params.append( key, value );
				}
			}
		} );

		if ( curPage > 1 ) {
			params.append( 'paged', curPage );
		}

		if ( perPage ) {
			params.append( 'per_page', perPage );
		}

		return params;
	}

	function updateFilterCounts() {
		const active = [];
		filterWrappers.forEach( group => {
			const checked = group.querySelectorAll( '.js-clearable:checked' );
			checked.forEach( input => {
				active.push( {
					name: input.name.replace( '[]', '' ),
					value: input.value,
					label: input.parentElement.querySelector( 'label' ).innerHTML,
				} );
			} );

			if ( checked.length > 0 ) {
				group.style.setProperty( '--count', `"${ checked.length }"` );
			} else {
				group.style.removeProperty( '--count' );
			}
		} );

		const params = getQueryParams();
		const isEmptyParams = params.size === 0 || ( params.has( 'sort' ) && params.size <= 1 );

		if ( applyButton ) {
			applyButton.hidden = isEmptyParams;
		}
		if ( clearButton ) {
			clearButton.hidden = isEmptyParams;
		}

		if ( filterCount ) {
			filterCount.dataset.count = active.length;
			filterCount.disabled = active.length === 0;
		}
		if ( resetButton ) {
			resetButton.disabled = active.length === 0;
		}

		if ( filterSelection ) {
			filterSelection.innerHTML = '';
			active.forEach( ( filter, index ) => {
				const id = `filter-clear-${ index }`;

				const item = document.createElement( 'li' );
				item.className = 'filter-control__option';

				const label = document.createElement( 'label' );
				label.htmlFor = id;
				label.innerHTML = filter.label;

				const button = document.createElement( 'button' );
				button.id = id;
				button.dataset.clear = `${ filter.name }:${ filter.value }`;
				button.innerHTML = '<span class="screen-reader-text">Remove Filter</span>';

				item.append( label );
				item.append( button );

				filterSelection.append( item );
			} );
		}
	}

	function syncFormsWithParams( params ) {
		if ( searchInput && ! params.get( 's' ) && ! params.get( 'search' ) ) {
			searchInput.value = '';
		}

		for ( const key of params.keys() ) {
			const selected = params.getAll( key );
			container.querySelectorAll( `[name="${ key }"]` ).forEach( input => {
				if ( input.type === 'radio' || input.type === 'checkbox' ) {
					input.checked = selected.includes( input.value );
				} else {
					input.value = selected[ 0 ];
				}
			} );
		}

		updateFilterCounts();
	}

	function fetchResults( params ) {
		syncFormsWithParams( params );

		params.append( 'format', format );
		params.append( 'columns', columns );

		container.classList.add( 'loading' );

		const url = new URL( fetchUrl );
		for ( const [ key, value ] of params.entries() ) {
			if ( value ) {
				url.searchParams.append( key, value );
			}
		}

		return fetch( url.toString() )
			.then( response => {
				if ( ! response.ok ) {
					throw new Error( `Directory Request Status: ${ response.status }` );
				}

				return response.json();
			} )
			.then( response => {
				if ( heading ) {
					heading.hidden = ! response.heading;
					heading.innerHTML = response.heading;
				}

				if ( statsWrapper ) {
					statsWrapper.hidden = ! response.stats;
					statsWrapper.innerHTML = response.stats;
				}

				if ( navWrapper ) {
					navWrapper.hidden = ! response.nav;
					navWrapper.innerHTML = response.nav;
				}

				if ( tagsWrapper ) {
					tagsWrapper.hidden = ! response.tags;
					tagsWrapper.innerHTML = response.tags;
				}

				if ( resultsWrapper ) {
					let results = response.results;
					if ( results instanceof Array ) {
						results = results.join( '\n' );
					}

					resultsWrapper.hidden = ! results;
					resultsWrapper.innerHTML = results;
				}

				paginationWrappers.forEach( wrapper => {
					wrapper.hidden = ! response.pagination;
					wrapper.innerHTML = response.pagination;
				} );

				container.classList.remove( 'loading' );
			} )
			.catch( () => {
				// do nothing
			} )
			.finally( () => {
				if ( window.scrollY > container.offsetTop ) {
					container.scrollIntoView( true );
				}
			} );
	}

	function fetchResultsFromForms( page = 1 ) {
		curPage = page;

		const params = getQueryParams();
		const query = params.toString();
		if ( baseUrl ) {
			const url = new URL( baseUrl );
			for ( const key of params.keys() ) {
				for ( const value of params.getAll( key ) ) {
					url.searchParams.append( key, value );
				}
			}
			history.pushState( { query }, '', url.toString() );
		}

		fetchResults( params );
	}

	forms.forEach( form => {
		form.addEventListener( 'submit', event => {
			event.preventDefault();
			fetchResultsFromForms();
		} );

		form.addEventListener( 'reset', event => {
			event.preventDefault();

			const inputs = container.querySelectorAll( 'input.js-clearable' );
			inputs.forEach( input => {
				if ( input.type === 'checkbox' || input.type === 'radio' ) {
					input.checked = false;
				} else {
					input.value = '';
				}
			} );

			updateFilterCounts();

			fetchResultsFromForms();
		} );
	} );

	updateFilterCounts();
	filterWrappers.forEach( filter => {
		filter.addEventListener( 'input', event => {
			const form = event.target.closest( 'form' );
			if ( form?.classList.contains( 'autosubmit' ) ) {
				if ( typeof form.requestSubmit === 'function' ) {
					form.requestSubmit();
				} else {
					form.dispatchEvent( new Event( 'submit' ) );
				}
			}
		} );
	} );

	searchInput?.addEventListener( 'search', debounce( function() {
		if ( this.value === '' ) {
			fetchResultsFromForms();
		}
	}, 100 ) );

	sortControl?.addEventListener( 'change', function() {
		fetchResultsFromForms();
	} );

	container.addEventListener( 'click', event => {
		const target = event.target.closest( 'a' ) || event.target;

		if ( target.dataset.page ) {
			event.preventDefault();
			fetchResultsFromForms( target.dataset.page );
		} else if ( target.dataset.filter ) {
			event.preventDefault();

			const [ name, value = null ] = target.dataset.filter.split( ':' );
			container.querySelectorAll( `[name="${ name }"]` ).forEach( input => {
				input.checked = input.value === value;
			} );

			fetchResultsFromForms();
		} else if ( target.dataset.clear ) {
			event.preventDefault();

			const [ name, value = null ] = target.dataset.clear.split( ':' );

			let inputs = container.querySelectorAll( 'input.js-clearable' );
			if ( name !== 'ALL' ) {
				inputs = container.querySelectorAll( `[name="${ name }"], [name="${ name }[]"]` );
			}

			inputs.forEach( input => {
				if ( value === null || input.value === value ) {
					if ( input.type === 'checkbox' || input.type === 'radio' ) {
						input.checked = false;
					} else {
						input.value = '';
					}
				}
			} );

			fetchResultsFromForms();
		}
	} );

	window.addEventListener( 'popstate', event => {
		if ( event.state.query ) {
			event.preventDefault();
			const params = new URLSearchParams( event.state.query );
			fetchResults( params );
		}
	} );
}

export default function setupDirectoryModules() {
	document.querySelectorAll( '.js-directory' ).forEach( setupDirectoryModule );
}
